import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EmbeddedViewRef,
  HostBinding,
  Input,
  isDevMode,
  IterableChangeRecord,
  IterableDiffer,
  IterableDiffers,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  TrackByFunction,
  ViewChild,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import { toBoolean } from '@ngx-simple/core/coercion';
import { ThemePalette } from '@ngx-simple/core/common-behaviors';
import { InputBoolean } from '@ngx-simple/core/decorators';
import { BehaviorSubject, isObservable, Observable, of, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SimColumnDefDirective } from './cell';
import { isDataSource } from './data-source';
import { SIM_TABLE } from './default-options';
import {
  BaseRowDef,
  SimCdkCellOutletDirective,
  SimCellOutletMultiRowContext,
  SimCellOutletRowContext,
  SimFooterRowDefDirective,
  SimHeaderRowDefDirective,
  SimRowDefDirective
} from './row';
import {
  getTableDuplicateColumnNameError,
  getTableMissingMatchingRowDefError,
  getTableMultipleDefaultRowDefsError,
  getTableUnknownColumnError
} from './table-errors';
import { RowOutlet, SimFooterRowOutletDirective, SimHeaderRowOutletDirective, SimRowOutletDirective } from './table.directive';

export interface RenderRow<T> {
  data: T;
  dataIndex: number;
  rowDef: SimRowDefDirective<T>;
}

export interface RowContext<T> extends SimCellOutletMultiRowContext<T>, SimCellOutletRowContext<T> {}

export abstract class RowViewRef<T> extends EmbeddedViewRef<RowContext<T>> {}

@Component({
  selector: 'table[sim-table]',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: SIM_TABLE,
      useExisting: SimTableComponent
    }
  ],
  host: {
    class: 'sim-table'
  }
})
export class SimTableComponent<T> implements OnInit, AfterContentChecked, OnDestroy {
  @Input() color: ThemePalette;

  /** 表格和其中的每个单元格增加边框 */
  @Input()
  @HostBinding('class.sim-table-bordered')
  @InputBoolean<SimTableComponent<T>, 'bordered'>()
  bordered: boolean = false;

  /** 给 <tbody> 之内的每一行增加斑马条纹样式 */
  @Input()
  @HostBinding('class.sim-table-striped')
  @InputBoolean<SimTableComponent<T>, 'striped'>()
  striped: boolean = false;

  /** 用于查找数据源提供的数据中的更改 */
  private _dataDiffer: IterableDiffer<RenderRow<T>>;

  @Input()
  get dataSource() {
    return this._dataSource;
  }
  set dataSource(value: any) {
    if (this._dataSource !== value) {
      this._switchDataSource(value);
    }
  }
  private _dataSource: T[];

  @Input()
  get trackBy(): TrackByFunction<T> {
    return this._trackByFn;
  }
  set trackBy(fn: TrackByFunction<T>) {
    if (isDevMode() && fn != null && typeof fn !== 'function' && <any>console && <any>console.warn) {
      console.warn(`trackBy must be a function, but received ${JSON.stringify(fn)}.`);
    }
    this._trackByFn = fn;
  }
  private _trackByFn: TrackByFunction<T>;

  @Input()
  get multiTemplateDataRows(): boolean {
    return this._multiTemplateDataRows;
  }
  set multiTemplateDataRows(v: boolean) {
    this._multiTemplateDataRows = toBoolean(v);
    if (this._rowOutlet && this._rowOutlet.viewContainer.length) {
      // this._forceRenderDataRows();
    }
  }
  _multiTemplateDataRows: boolean = false;

  @ViewChild(SimRowOutletDirective, { static: true })
  _rowOutlet: SimRowOutletDirective;

  @ViewChild(SimHeaderRowOutletDirective, { static: true })
  _headerRowOutlet: SimHeaderRowOutletDirective;

  @ViewChild(SimFooterRowOutletDirective, { static: true })
  _footerRowOutlet: SimFooterRowOutletDirective;

  @ViewChildren(SimColumnDefDirective)
  _presetColumnDefs: QueryList<SimColumnDefDirective>;

  /** 获取 <ng-container simColumnDef="field"></ng-container> */
  @ContentChildren(SimColumnDefDirective, { descendants: true })
  _contentColumnDefs: QueryList<SimColumnDefDirective>;

  /** 获取 <tr sim-row *simRowDef="let row; columns: [field, field...]"></tr> */
  @ContentChildren(SimRowDefDirective, { descendants: true })
  _contentRowDefs: QueryList<SimRowDefDirective<T>>;

  /** 获取 <tr sim-header-row *simHeaderRowDef="[field, field...]"></tr> */
  @ContentChildren(SimHeaderRowDefDirective, {
    descendants: true
  })
  _contentHeaderRowDefs: QueryList<SimHeaderRowDefDirective<T>>;

  /** 获取 <tr sim-footer-row *simFooterRowDef="[field, field...]"></tr> */
  @ContentChildren(SimFooterRowDefDirective, {
    descendants: true
  })
  _contentFooterRowDefs: QueryList<SimFooterRowDefDirective<T>>;

  viewChange: BehaviorSubject<{ start: number; end: number }> = new BehaviorSubject<{ start: number; end: number }>({
    start: 0,
    end: Number.MAX_VALUE
  });

  /** table._data的引用，提供给column组件扩展 我们不允许直接改写 不提供`set data` */
  get data() {
    return this._data;
  }

  private _columnDefsByName = new Map<string, SimColumnDefDirective>();

  private _cachedRenderRowsMap = new Map<T, WeakMap<SimRowDefDirective<T>, RenderRow<T>[]>>();

  /** 此表可使用的所有行定义的集合。由使用'ContentChildren'收集的行以及添加到'_customRowDefs'中的自定义行填充 */
  private _rowDefs: SimRowDefDirective<T>[];
  private _headerRowDefs: SimHeaderRowDefDirective<T>[];
  private _footerRowDefs: SimFooterRowDefDirective<T>[];

  /** 存储没有when谓词的行定义 */
  private _defaultRowDef: SimRowDefDirective<T> | null;

  /** 数据源提供的最新数据 */
  private _data: T[] | ReadonlyArray<T>;

  private _renderRows: RenderRow<T>[];

  /** 侦听数据源提供的数据的订阅 */
  private _renderChangeSubscription: Subscription | null;

  /** 当组件被销毁时发送的主题 */
  private _destroyed$ = new Subject<void>();

  /**
   * Column definitions that were defined outside of the direct content children of the table.
   * These will be defined when, e.g., creating a wrapper around the cdkTable that has
   * column definitions as *its* content child.
   */
  private _customColumnDefs = new Set<SimColumnDefDirective>();

  /**
   * Data row definitions that were defined outside of the direct content children of the table.
   * These will be defined when, e.g., creating a wrapper around the cdkTable that has
   * built-in data rows as *its* content child.
   */
  private _customRowDefs = new Set<SimRowDefDirective<T>>();

  /**
   * Header row definitions that were defined outside of the direct content children of the table.
   * These will be defined when, e.g., creating a wrapper around the cdkTable that has
   * built-in header rows as *its* content child.
   */
  private _customHeaderRowDefs = new Set<SimHeaderRowDefDirective<T>>();

  /**
   * Footer row definitions that were defined outside of the direct content children of the table.
   * These will be defined when, e.g., creating a wrapper around the cdkTable that has a
   * built-in footer row as *its* content child.
   */
  private _customFooterRowDefs = new Set<SimFooterRowDefDirective<T>>();

  /**
   * 标题行定义是否已更改。在检查内容后触发对标题行的更新。初始化为true，以便表呈现初始的行集。
   */
  private _headerRowDefChanged = true;

  /**
   * 页脚行定义是否已更改。在检查内容后触发对页脚行的更新。初始化为true，以便表呈现初始的行集。
   */
  private _footerRowDefChanged = true;

  constructor(private _differs: IterableDiffers, private _changeDetectorRef: ChangeDetectorRef) {}

  ngOnInit(): void {
    this._dataDiffer = this._differs.find([]).create((_i: number, dataRow: RenderRow<T>) => {
      return this.trackBy ? this.trackBy(dataRow.dataIndex, dataRow.data) : dataRow;
    });
  }

  ngAfterContentChecked(): void {
    // 缓存由ContentChildren和编程注入收集的行和列定义
    this._cacheRowDefs();
    this._cacheColumnDefs();

    // 确保用户至少添加了页眉、页脚或数据行def
    if (!this._headerRowDefs.length && !this._footerRowDefs.length && !this._rowDefs.length) {
      throw Error('Missing definitions for header, footer, and row; cannot determine which columns should be rendered.');
    }

    // 如果标题、行或页脚defs的列列表被更改，则呈现更新。
    // this._renderUpdatedColumns();

    // If the header row definition has been changed, trigger a render to the header row.
    if (this._headerRowDefChanged) {
      this._forceRenderHeaderRows();
      this._headerRowDefChanged = false;
    }

    // If the footer row definition has been changed, trigger a render to the footer row.
    if (this._footerRowDefChanged) {
      this._forceRenderFooterRows();
      this._footerRowDefChanged = false;
    }

    // this.renderRows();
    // this._forceRenderHeaderRows();
    // this._forceRenderFooterRows();

    // 如果有数据源和行定义，除非已经建立连接，否则连接到数据源。
    if (this.dataSource && this._rowDefs.length > 0 && !this._renderChangeSubscription) {
      this._observeRenderChanges();
    }

    this._checkStickyStates();
  }

  ngOnDestroy(): void {
    this._rowOutlet.viewContainer.clear();
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  /**
   * Checks if there has been a change in sticky states since last check and applies the correct
   * sticky styles. Since checking resets the "dirty" state, this should only be performed once
   * during a change detection and after the inputs are settled (after content check).
   */
  private _checkStickyStates() {
    // const stickyCheckReducer = (acc: boolean, d: SimHeaderRowDefDirective<T> | SimFooterRowDefDirective<T> | SimRowDefDirective<T>) => {
    //   return acc || d.hasStickyChanged();
    // };
    // Note that the check needs to occur for every definition since it notifies the definition
    // that it can reset its dirty state. Using another operator like `some` may short-circuit
    // remaining definitions and leave them in an unchecked state.
    // if (this._headerRowDefs.reduce(stickyCheckReducer, false)) {
    //   this.updateStickyHeaderRowStyles();
    // }
    // if (this._footerRowDefs.reduce(stickyCheckReducer, false)) {
    //   this.updateStickyFooterRowStyles();
    // }
    // if (Array.from(this._columnDefsByName.values()).reduce(stickyCheckReducer, false)) {
    //   this.updateStickyColumnStyles();
    // }
  }

  /** Adds a column definition that was not included as part of the content children. */
  addColumnDef(columnDef: SimColumnDefDirective) {
    this._customColumnDefs.add(columnDef);
  }

  /** Removes a column definition that was not included as part of the content children. */
  removeColumnDef(columnDef: SimColumnDefDirective) {
    this._customColumnDefs.delete(columnDef);
  }

  /** Adds a row definition that was not included as part of the content children. */
  addRowDef(rowDef: SimRowDefDirective<T>) {
    this._customRowDefs.add(rowDef);
  }

  /** Removes a row definition that was not included as part of the content children. */
  removeRowDef(rowDef: SimRowDefDirective<T>) {
    this._customRowDefs.delete(rowDef);
  }

  /** 添加未作为内容子元素的一部分包含的标题行定义 */
  addHeaderRowDef(headerRowDef: SimHeaderRowDefDirective<T>) {
    this._customHeaderRowDefs.add(headerRowDef);
    this._headerRowDefChanged = true;
  }

  /** 删除未作为内容子元素包含的标题行定义 */
  removeHeaderRowDef(headerRowDef: SimHeaderRowDefDirective<T>) {
    this._customHeaderRowDefs.delete(headerRowDef);
    this._headerRowDefChanged = true;
  }

  /** Adds a footer row definition that was not included as part of the content children. */
  addFooterRowDef(footerRowDef: SimFooterRowDefDirective<T>) {
    this._customFooterRowDefs.add(footerRowDef);
    this._footerRowDefChanged = true;
  }

  /** Removes a footer row definition that was not included as part of the content children. */
  removeFooterRowDef(footerRowDef: SimFooterRowDefDirective<T>) {
    this._customFooterRowDefs.delete(footerRowDef);
    this._footerRowDefChanged = true;
  }

  renderRows() {
    this._renderRows = this._getAllRenderRows();
    const changes = this._dataDiffer.diff(this._renderRows);
    if (!changes) {
      return;
    }

    const viewContainer = this._rowOutlet.viewContainer;

    changes.forEachOperation((record: IterableChangeRecord<RenderRow<T>>, prevIndex: number | null, currentIndex: number | null) => {
      if (record.previousIndex == null) {
        this._insertRow(record.item, currentIndex);
      } else if (currentIndex == null) {
        viewContainer.remove(prevIndex);
      } else {
        const view = viewContainer.get(prevIndex);
        viewContainer.move(view, currentIndex);
      }
    });

    this._updateRowIndexContext();

    changes.forEachIdentityChange((record: IterableChangeRecord<RenderRow<T>>) => {
      const rowView = viewContainer.get(record.currentIndex) as RowViewRef<T>;
      rowView.context.$implicit = record.item.data;
    });
  }

  /** 获取已在行出口中呈现的行列表 */
  _getRenderedRows(rowOutlet: RowOutlet): HTMLElement[] {
    const renderedRows: HTMLElement[] = [];
    const viewContainer = rowOutlet.viewContainer;
    for (let i = 0, length = viewContainer.length; i < length; i++) {
      const viewRef = viewContainer.get(i) as EmbeddedViewRef<any>;
      renderedRows.push(viewRef.rootNodes[0]);
    }
    return renderedRows;
  }

  _getRowDefs(data: T, dataIndex: number): SimRowDefDirective<T>[] {
    if (this._rowDefs.length === 1) {
      return [this._rowDefs[0]];
    }

    let rowDefs: SimRowDefDirective<T>[] = [];
    if (this.multiTemplateDataRows) {
      rowDefs = this._rowDefs.filter(def => !def.when || def.when(dataIndex, data));
    } else {
      const rowDef = this._rowDefs.find(def => def.when && def.when(dataIndex, data)) || this._defaultRowDef;
      if (rowDef) {
        rowDefs.push(rowDef);
      }
    }

    if (!rowDefs.length) {
      throw getTableMissingMatchingRowDefError(data);
    }

    return rowDefs;
  }

  private _forceRenderHeaderRows() {
    // 如果存在任何内容，则清除标题行出口
    if (this._headerRowOutlet.viewContainer.length > 0) {
      this._headerRowOutlet.viewContainer.clear();
    }
    this._headerRowDefs.forEach((def, i) => this._renderRow(this._headerRowOutlet, def, i));
  }

  private _forceRenderFooterRows() {
    // 如果存在任何内容，则清除标题行出口
    if (this._footerRowOutlet.viewContainer.length > 0) {
      this._footerRowOutlet.viewContainer.clear();
    }
    this._footerRowDefs.forEach((def, i) => this._renderRow(this._footerRowOutlet, def, i));
  }

  private _updateRowIndexContext() {
    const viewContainer = this._rowOutlet.viewContainer;
    for (let renderIndex = 0, count = viewContainer.length; renderIndex < count; renderIndex++) {
      const viewRef = viewContainer.get(renderIndex) as RowViewRef<T>;
      const context = viewRef.context as RowContext<T>;
      context.count = count;
      context.first = renderIndex === 0;
      context.last = renderIndex === count - 1;
      context.even = renderIndex % 2 === 0;
      context.odd = !context.even;

      if (this.multiTemplateDataRows) {
        context.dataIndex = this._renderRows[renderIndex].dataIndex;
        context.renderIndex = renderIndex;
      } else {
        context.index = this._renderRows[renderIndex].dataIndex;
      }
    }
  }

  private _getAllRenderRows(): RenderRow<T>[] {
    const renderRows: RenderRow<T>[] = [];

    const prevCachedRenderRows = this._cachedRenderRowsMap;
    this._cachedRenderRowsMap = new Map();

    for (let i = 0; i < this._data.length; i++) {
      const data = this._data[i];
      const renderRowsForData = this._getRenderRowsForData(data, i, prevCachedRenderRows.get(data));

      if (!this._cachedRenderRowsMap.has(data)) {
        this._cachedRenderRowsMap.set(data, new WeakMap());
      }

      for (let j = 0; j < renderRowsForData.length; j++) {
        const renderRow = renderRowsForData[j];

        const cache = this._cachedRenderRowsMap.get(renderRow.data);
        if (cache.has(renderRow.rowDef)) {
          cache.get(renderRow.rowDef).push(renderRow);
        } else {
          cache.set(renderRow.rowDef, [renderRow]);
        }
        renderRows.push(renderRow);
      }
    }

    return renderRows;
  }

  private _getRenderRowsForData(data: T, dataIndex: number, cache?: WeakMap<SimRowDefDirective<T>, RenderRow<T>[]>): RenderRow<T>[] {
    const rowDefs = this._getRowDefs(data, dataIndex);

    return rowDefs.map(rowDef => {
      const cachedRenderRows = cache && cache.has(rowDef) ? cache.get(rowDef) : [];
      if (cachedRenderRows.length) {
        const dataRow = cachedRenderRows.shift();
        dataRow.dataIndex = dataIndex;
        return dataRow;
      } else {
        return { data, rowDef, dataIndex };
      }
    });
  }

  private _cacheRowDefs() {
    this._headerRowDefs = this._getOwnDefs(this._contentHeaderRowDefs);
    this._footerRowDefs = this._getOwnDefs(this._contentFooterRowDefs);

    this._rowDefs = this._getOwnDefs(this._contentRowDefs);

    /** 确定所有行定义之后，查找默认的行定义 */
    const defaultRowDefs = this._rowDefs.filter(def => !def.when);
    if (!this.multiTemplateDataRows && defaultRowDefs.length > 1) {
      throw getTableMultipleDefaultRowDefsError();
    }
    this._defaultRowDef = defaultRowDefs[0];
  }

  /** 更新包含内容列定义的映射 */
  private _cacheColumnDefs() {
    this._columnDefsByName.clear();
    const columnDefs = mergeArrayAndSet(this._getOwnDefs(this._contentColumnDefs), this._customColumnDefs);

    if (this._presetColumnDefs) {
      this._presetColumnDefs.forEach(element => columnDefs.push(element));
    }

    columnDefs.forEach(columnDef => {
      if (this._columnDefsByName.has(columnDef.name)) {
        throw getTableDuplicateColumnNameError(columnDef.name);
      }
      this._columnDefsByName.set(columnDef.name, columnDef);
    });
  }

  private _switchDataSource(dataSource: any) {
    this._data = [];

    if (isDataSource(this.dataSource)) {
      this.dataSource.disconnect(this);
    }

    // 停止监听来自前一个数据源的数据。
    if (this._renderChangeSubscription) {
      this._renderChangeSubscription.unsubscribe();
      this._renderChangeSubscription = null;
    }

    if (!dataSource) {
      if (this._dataDiffer) {
        this._dataDiffer.diff([]);
      }
      this._rowOutlet.viewContainer.clear();
    }
    this._dataSource = dataSource;
  }

  /** 为数据源提供的数据设置订阅 */
  private _observeRenderChanges() {
    // 如果没有设置数据源，则无需观察任何更改
    if (!this.dataSource) {
      return;
    }

    let dataStream: Observable<T[] | ReadonlyArray<T>> | undefined;

    if (isDataSource(this.dataSource)) {
      dataStream = this.dataSource.connect(this);
    } else if (isObservable<T[]>(this.dataSource)) {
      dataStream = this.dataSource;
    } else if (Array.isArray(this.dataSource)) {
      dataStream = of(this.dataSource);
    }

    if (dataStream === undefined) {
      throw Error(`Provided data source did not match an array, Observable, or DataSource`);
    }

    this._renderChangeSubscription = dataStream.pipe(takeUntil(this._destroyed$)).subscribe(data => {
      this._data = data || [];
      this.renderRows();
    });
  }

  /** 为数据行模板创建嵌入视图，并将其放在数据行视图容器中的正确索引位置。 */
  private _insertRow(renderRow: RenderRow<T>, renderIndex: number) {
    const rowDef = renderRow.rowDef;
    const context: RowContext<T> = { $implicit: renderRow.data };
    this._renderRow(this._rowOutlet, rowDef, renderIndex, context);
  }

  private _renderRow(outlet: RowOutlet, rowDef: BaseRowDef, index: number, context: RowContext<T> = {}) {
    outlet.viewContainer.createEmbeddedView(rowDef.template, context, index);
    for (const cellTemplate of this._getCellTemplates(rowDef)) {
      if (SimCdkCellOutletDirective.mostRecentCellOutlet) {
        SimCdkCellOutletDirective.mostRecentCellOutlet._viewContainer.createEmbeddedView(cellTemplate, context);
      }
    }

    this._changeDetectorRef.markForCheck();
  }

  /** 获取所提供的行定义的列定义 */
  private _getCellTemplates(rowDef: BaseRowDef): TemplateRef<any>[] {
    if (!rowDef || !rowDef.columns) {
      return [];
    }
    return Array.from(rowDef.columns, columnId => {
      const column = this._columnDefsByName.get(columnId);

      if (!column) {
        throw getTableUnknownColumnError(columnId);
      }
      return rowDef.extractCellTemplate(column);
    });
  }

  /** 从QueryList中筛选属于此表的定义 */
  private _getOwnDefs<I extends { _table?: any }>(items: QueryList<I>): I[] {
    return items.filter(item => !item._table || item._table === this);
  }
}

function mergeArrayAndSet<T>(array: T[], set: Set<T>): T[] {
  return array.concat(Array.from(set));
}
